import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import API from '../services/api';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const location = useLocation();
  const getTabFromQuery = (search) => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'colleges') return 'colleges';
    if (tab === 'companies') return 'companies';
    return 'stats';
  };

  const [activeTab, setActiveTab] = useState(getTabFromQuery(location.search));
  const [stats, setStats] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [newCollege, setNewCollege] = useState({
    name: '', code: '', contact_email: '',
    adminName: '', adminEmail: '', adminPassword: ''
  });
  const [companies, setCompanies] = useState([]);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '', pattern_description: '', difficulty_level: 'medium'
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    setActiveTab(getTabFromQuery(location.search));
  }, [location]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'stats') {
        const res = await API.get('/colleges/stats');
        setStats(res.data);
      } else if (activeTab === 'colleges') {
        const res = await API.get('/colleges');
        setColleges(res.data);
      } else if (activeTab === 'companies') {
        const res = await API.get('/companies/admin');
        setCompanies(res.data);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollege = async (e) => {
    e.preventDefault();
    try {
      await API.post('/colleges', newCollege);
      setShowModal(false);
      setNewCollege({ name: '', code: '', contact_email: '', adminName: '', adminEmail: '', adminPassword: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating college');
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      await API.post('/companies', newCompany);
      setShowCompanyModal(false);
      setNewCompany({ name: '', pattern_description: '', difficulty_level: 'medium' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.message || 'Error creating company');
    }
  };

  const toggleCompanyStatus = async (id, currentStatus) => {
    try {
      await API.put(`/companies/${id}`, { is_active: !currentStatus });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleCollegeStatus = async (id, currentStatus) => {
    try {
      await API.put(`/colleges/${id}`, { is_active: !currentStatus });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="super-admin-page">
      <div className="super-header">
        <div>
          <h1>Platform Administration</h1>
          <p>Global oversight of Zaptude ecosystem</p>
        </div>
        <div className="tab-switcher">
          <button 
            className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            📊 System Stats
          </button>
          <button 
            className={`tab-btn ${activeTab === 'colleges' ? 'active' : ''}`}
            onClick={() => setActiveTab('colleges')}
          >
            🏛️ Manage Colleges
          </button>
          <button 
            className={`tab-btn ${activeTab === 'companies' ? 'active' : ''}`}
            onClick={() => setActiveTab('companies')}
          >
            🏢 Manage Companies
          </button>
        </div>
      </div>

      {activeTab === 'stats' && (
        <div className="stats-view">
          {loading && !stats ? <div className="loader">Loading stats...</div> : (
            <>
              <div className="super-stats-grid">
                <div className="super-stat-card accent-navy">
                  <div className="stat-tile-icon">🏛️</div>
                  <div className="stat-body">
                    <div className="stat-val">{stats?.total_colleges ?? 0}</div>
                    <div className="stat-label">Total Colleges</div>
                    <div className="stat-sub">{stats?.active_colleges ?? 0} active</div>
                  </div>
                </div>
                <div className="super-stat-card accent-purple">
                  <div className="stat-tile-icon">👥</div>
                  <div className="stat-body">
                    <div className="stat-val">{stats?.total_users ?? 0}</div>
                    <div className="stat-label">System Users</div>
                    <div className="stat-sub">{stats?.total_students ?? 0} students · {stats?.total_admins ?? 0} admins</div>
                  </div>
                </div>
                <div className="super-stat-card accent-teal">
                  <div className="stat-tile-icon">📝</div>
                  <div className="stat-body">
                    <div className="stat-val">{stats?.total_tests ?? 0}</div>
                    <div className="stat-label">Tests Created</div>
                    <div className="stat-sub">{stats?.total_questions ?? 0} questions in bank</div>
                  </div>
                </div>
                <div className="super-stat-card accent-orange">
                  <div className="stat-tile-icon">📈</div>
                  <div className="stat-body">
                    <div className="stat-val">{stats?.total_results ?? 0}</div>
                    <div className="stat-label">Results Generated</div>
                    <div className="stat-sub">{stats?.total_companies ?? 0} companies onboarded</div>
                  </div>
                </div>
              </div>

              <div className="stats-info-row">
                <div className="info-card">
                  <div className="info-card-head">
                    <h3>User Distribution</h3>
                    <span className="info-card-total">{stats?.total_users ?? 0} total</span>
                  </div>
                  {(() => {
                    const students = stats?.total_students || 0;
                    const admins = stats?.total_admins || 0;
                    const known = students + admins;
                    const others = Math.max(0, (stats?.total_users || 0) - known);
                    const pct = (n) => stats?.total_users ? Math.round((n / stats.total_users) * 100) : 0;
                    return (
                      <>
                        <div className="dist-bar">
                          <span className="dist-seg students" style={{ width: `${pct(students)}%` }} />
                          <span className="dist-seg admins" style={{ width: `${pct(admins)}%` }} />
                          <span className="dist-seg others" style={{ width: `${pct(others)}%` }} />
                        </div>
                        <div className="dist-legend">
                          <div className="dist-leg"><span className="dot students" /> Students <b>{students}</b></div>
                          <div className="dist-leg"><span className="dot admins" /> Admins <b>{admins}</b></div>
                          <div className="dist-leg"><span className="dot others" /> Other <b>{others}</b></div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="info-card">
                  <div className="info-card-head">
                    <h3>Platform Averages</h3>
                  </div>
                  <div className="avg-metrics">
                    <div className="avg-metric">
                      <span className="avg-val">
                        {stats?.total_colleges ? Math.round((stats.total_users || 0) / stats.total_colleges) : 0}
                      </span>
                      <span className="avg-label">Users / College</span>
                    </div>
                    <div className="avg-metric">
                      <span className="avg-val">
                        {stats?.total_tests ? ((stats.total_results || 0) / stats.total_tests).toFixed(1) : '0.0'}
                      </span>
                      <span className="avg-label">Results / Test</span>
                    </div>
                    <div className="avg-metric">
                      <span className="avg-val">
                        {stats?.total_colleges
                          ? Math.round(((stats.active_colleges || 0) / stats.total_colleges) * 100)
                          : 0}%
                      </span>
                      <span className="avg-label">Colleges Active</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="platform-health-card">
                <h3>Platform Integrity</h3>
                <div className="health-metrics">
                  <div className="metric">
                    <span>Database Status</span>
                    <span className="status-badge online">Optimal</span>
                  </div>
                  <div className="metric">
                    <span>API Performance</span>
                    <span className="status-badge online">Normal (120ms)</span>
                  </div>
                  <div className="metric">
                    <span>Multi-Tenancy Scoping</span>
                    <span className="status-badge security">Enforced</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === 'colleges' && (
        <div className="colleges-view">
          <div className="view-header">
            <h3>Registered Institutions <span className="view-count-pill">{colleges.length}</span></h3>
            <button className="add-college-btn" onClick={() => setShowModal(true)}>
              + Onboard New College
            </button>
          </div>

          <div className="college-list-card">
            {loading ? <div className="loader">Updating list...</div> : (
              <table className="super-table">
                <thead>
                  <tr>
                    <th>College Name</th>
                    <th>Subdomain</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {colleges.map(college => (
                    <tr key={college.id}>
                      <td>
                        <div className="college-name-cell">
                          <span className="college-avatar">{college.name ? college.name.charAt(0).toUpperCase() : '?'}</span>
                          <span className="college-name-text">{college.name}</span>
                        </div>
                      </td>
                      <td><span className="subdomain-chip">{college.code.toLowerCase()}</span></td>
                      <td><span className="users-pill">👥 {college.users?.[0]?.count || 0}</span></td>
                      <td>
                        <span className={`status-badge ${college.is_active ? 'online' : 'offline'}`}>
                          {college.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`action-btn ${college.is_active ? 'danger' : 'success'}`}
                          onClick={() => toggleCollegeStatus(college.id, college.is_active)}
                        >
                          {college.is_active ? 'Suspend' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'companies' && (
        <div className="companies-view">
          <div className="view-header">
            <h3>Global Institutions List</h3>
            <button className="add-college-btn" onClick={() => setShowCompanyModal(true)}>
              + Add New Company
            </button>
          </div>

          <div className="college-list-card">
            {loading ? <div className="loader">Updating companies...</div> : (
              <table className="super-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Pattern Info</th>
                    <th>Difficulty</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => (
                    <tr key={company.id}>
                      <td className="bold">{company.name}</td>
                      <td>
                         <div className="truncate-text" title={company.pattern_description}>
                            {company.pattern_description || 'No description added'}
                         </div>
                      </td>
                      <td>
                        <span className={`diff-badge sm ${company.difficulty_level}`}>
                          {company.difficulty_level}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${company.is_active ? 'online' : 'offline'}`}>
                          {company.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="action-btn toggle"
                          onClick={() => toggleCompanyStatus(company.id, company.is_active)}
                        >
                          {company.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="super-modal">
            <div className="modal-header">
              <h2>Onboard New College</h2>
              <button className="close-modal" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddCollege}>
              <div className="form-section">
                <h4>Institutional Details</h4>
                <div className="form-row">
                  <input type="text" placeholder="College Name" required
                    value={newCollege.name} onChange={e => setNewCollege({...newCollege, name: e.target.value})} />
                  <input type="text" placeholder="Subdomain Name (e.g. npsb)" required
                    value={newCollege.code} onChange={e => setNewCollege({...newCollege, code: e.target.value})} />
                </div>
                <input type="email" placeholder="Official Contact Email" required
                  value={newCollege.contact_email} onChange={e => setNewCollege({...newCollege, contact_email: e.target.value})} />
              </div>

              <div className="form-section">
                <h4>Primary Admin Credentials</h4>
                <input type="text" placeholder="Admin Name" required
                   value={newCollege.adminName} onChange={e => setNewCollege({...newCollege, adminName: e.target.value})} />
                <div className="form-row">
                  <input type="email" placeholder="Admin Email" required
                     value={newCollege.adminEmail} onChange={e => setNewCollege({...newCollege, adminEmail: e.target.value})} />
                  <input type="password" placeholder="Temp Password" required
                     value={newCollege.adminPassword} onChange={e => setNewCollege({...newCollege, adminPassword: e.target.value})} />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="save-btn">Create Institution</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCompanyModal && (
        <div className="modal-overlay">
          <div className="super-modal">
            <div className="modal-header">
              <h2>Add New Platform Company</h2>
              <button className="close-modal" onClick={() => setShowCompanyModal(false)}>×</button>
            </div>
            <form onSubmit={handleAddCompany}>
              <div className="form-section">
                <label>Company Name</label>
                <input type="text" placeholder="e.g. TCS, Infosys, Google" required
                  value={newCompany.name} onChange={e => setNewCompany({...newCompany, name: e.target.value})} />
                
                <label style={{ marginTop: '15px', display: 'block' }}>Pattern Description</label>
                <textarea rows={3} placeholder="Describe the exam pattern or recruitment process..."
                  value={newCompany.pattern_description} onChange={e => setNewCompany({...newCompany, pattern_description: e.target.value})} />

                <label style={{ marginTop: '15px', display: 'block' }}>Platform Difficulty Rating</label>
                <select value={newCompany.difficulty_level} onChange={e => setNewCompany({...newCompany, difficulty_level: e.target.value})}>
                   <option value="easy">Easy</option>
                   <option value="medium">Medium</option>
                   <option value="hard">Hard</option>
                </select>
              </div>

              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setShowCompanyModal(false)}>Cancel</button>
                <button type="submit" className="save-btn">Add Company</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
